export type ConsentValue = 'accepted' | 'rejected';

export interface ConsentPreferences {
  essential: true;
  thirdParty: boolean;
}

const COOKIE_NAME = 'rb_analytics_consent';
const PREFS_COOKIE = 'rb_consent_prefs';
const MAX_AGE = 365 * 24 * 60 * 60;

export function getAnalyticsConsent(): ConsentValue | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (!match) return null;
  const val = decodeURIComponent(match[1]);
  return val === 'accepted' || val === 'rejected' ? val : null;
}

export function setAnalyticsConsent(value: ConsentValue): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
}

/**
 * Opening state of the preferences panel under the OPT-IN regime, which is
 * also the fallback everywhere. `thirdParty` MUST stay false: a pre-ticked
 * non-essential category is not consent (CJEU C-673/17, Planet49), so the
 * optional toggle has to open off and the user has to turn it on themselves.
 */
export const DEFAULT_PREFS: ConsentPreferences = {
  essential: true,
  thirdParty: false,
};

/* ------------------------------------------------------------------ */
/* Regime                                                             */
/* ------------------------------------------------------------------ */

/**
 * Which consent law applies to this visitor.
 *
 *   'opt-in'  — non-essential trackers need PRIOR consent. Panel opens with
 *               the optional category off, and refusing costs exactly one
 *               click on the first layer.
 *   'opt-out' — trackers may run until the visitor objects (the US
 *               "Do Not Sell or Share" model). Panel may open with the
 *               optional category on.
 *
 * Splitting this is what lets the US surface run the higher-converting
 * opt-out flow WITHOUT exporting it to jurisdictions where a pre-ticked box
 * is not consent and a refusal buried behind a preferences panel is the
 * asymmetry CNIL fined Google and Meta over in January 2022.
 */
export type ConsentRegime = 'opt-in' | 'opt-out';

/**
 * EEA + UK + Switzerland: prior-consent jurisdictions for non-essential
 * cookies (ePrivacy Directive Art. 5(3) as implemented, UK PECR, Swiss revFADP
 * read with the ePrivacy-aligned telecoms rules).
 *
 * Deliberately conservative. Countries with consent-ish regimes that are less
 * settled (Brazil's LGPD, Japan's APPI, Canada's PIPEDA) are NOT enumerated
 * here — they fall through to the `opt-in` default below rather than being
 * granted the opt-out flow on a debatable reading.
 */
const OPT_IN_COUNTRIES: ReadonlySet<string> = new Set([
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA non-EU
  'IS', 'LI', 'NO',
  // UK + Switzerland
  'GB', 'CH',
]);

/**
 * Fallback opt-out set, used when no configured list is supplied.
 *
 * The live list is the `consent.optOutMarkets` platform setting, so widening
 * it is an audited runtime flip rather than a deploy — the same shape as
 * `purchases.markets`, and for the same reason: which countries may run the
 * looser flow is a legal call that changes on counsel's timeline, not ours.
 */
const DEFAULT_OPT_OUT_COUNTRIES: readonly string[] = ['US'];

/**
 * @param country       ISO-3166 alpha-2 from the ingress `x-geo-country`.
 * @param optOutMarkets Configured opt-out list. Omitted → the coded default.
 *
 * Precedence is deliberate: the hard-coded {@link OPT_IN_COUNTRIES} set is
 * checked FIRST and cannot be overridden by configuration. An admin widening
 * `consent.optOutMarkets` can therefore never put an EEA/UK/CH visitor into
 * the opt-out flow, however the list is edited — a mis-typed `DE` is inert
 * rather than an enforcement problem.
 *
 * Everything else — unknown country, malformed value, country in neither list
 * — resolves to `opt-in`. `x-geo-country` is unforgeable but it CAN be absent
 * (health probe, misconfigured edge, new region), and defaulting the other way
 * would turn one infrastructure gap into a consent failure for every EU
 * visitor at once.
 */
export function resolveConsentRegime(
  country: string | null | undefined,
  optOutMarkets?: readonly string[] | null,
): ConsentRegime {
  if (!country) return 'opt-in';
  const cc = country.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return 'opt-in';
  // Hard-coded opt-in jurisdictions win over any configured list.
  if (OPT_IN_COUNTRIES.has(cc)) return 'opt-in';
  const configured = Array.isArray(optOutMarkets)
    ? optOutMarkets
    : DEFAULT_OPT_OUT_COUNTRIES;
  return configured.some((m) => String(m).trim().toUpperCase() === cc)
    ? 'opt-out'
    : 'opt-in';
}

/**
 * True when the visitor is in a prior-consent jurisdiction (EEA, UK,
 * Switzerland: {@link OPT_IN_COUNTRIES}), OR when the country is absent or
 * malformed. Unknown fails toward "applies", the same direction as
 * `resolveConsentRegime`, so an ingress gap can never make an EEA visitor look
 * like one the rules do not cover.
 *
 * Added for the ads mediation (lib/ads/privacy.ts, ads-build-plan.md section
 * 5.4), where "GDPR applies and there is no TCF consent string" keeps the
 * programmatic source off. Kept here rather than copied so the ads code and
 * the consent banner can never disagree about which countries are covered.
 */
export function priorConsentApplies(country: string | null | undefined): boolean {
  if (!country) return true;
  const cc = country.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return true;
  return OPT_IN_COUNTRIES.has(cc);
}

/** Opening state of the preferences panel for a regime. */
export function defaultPrefsFor(regime: ConsentRegime): ConsentPreferences {
  return regime === 'opt-out'
    ? { essential: true, thirdParty: true }
    : { ...DEFAULT_PREFS };
}

export const ALL_ACCEPTED: ConsentPreferences = {
  essential: true,
  thirdParty: true,
};

/** Result of "Reject all": essential only. */
export const NONE_ACCEPTED: ConsentPreferences = {
  essential: true,
  thirdParty: false,
};

export function getConsentPreferences(): ConsentPreferences | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${PREFS_COOKIE}=([^;]*)`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

export function setConsentPreferences(prefs: ConsentPreferences): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${PREFS_COOKIE}=${encodeURIComponent(JSON.stringify(prefs))}; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
}
